import { StoreConfig, StoreConfigOptions } from '@datorama/akita';
import { registerPersistedStore } from './persist-registry';

/**
 * Options of `@PersistedStoreConfig`: everything Akita's `@StoreConfig` accepts, plus the
 * persistence/realtime metadata consumed by the persist registry (see persist-registry.ts).
 */
export type PersistedStoreConfigOptions = StoreConfigOptions & {
  /** Canonical realtime V2 protocol name for this store's entities. */
  realtimeEntity?: string;
  /** Additional accepted protocol names (entity -> store direction only). */
  realtimeEntityAliases?: string[];
  /** Also persist the Akita UI store child (`UI/<name>`). */
  persistUiStore?: boolean;
  /** 'external' when a dedicated persistState with custom hooks exists in localforage.settings.ts. */
  persistence?: 'standard' | 'external';
};

/**
 * Drop-in replacement for Akita's `@StoreConfig` that also registers the store for persistence on
 * IDB (and, optionally, in the realtime V2 entity-name mapping). Registration happens when the
 * decorator factory runs, i.e. at module evaluation — see persist-registry.ts for the timing
 * contract with the hydration gate.
 *
 * The class is returned UNCHANGED (no wrapping/subclassing): Angular's AOT-compiled metadata on the
 * store class must not be disturbed.
 */
// Decorator factory: PascalCase on purpose, like Akita's StoreConfig.
// eslint-disable-next-line @typescript-eslint/naming-convention
export function PersistedStoreConfig(options: PersistedStoreConfigOptions): ClassDecorator {
  const { realtimeEntity, realtimeEntityAliases, persistUiStore, persistence, ...storeOptions } = options;
  registerPersistedStore({ name: options.name, realtimeEntity, realtimeEntityAliases, persistUiStore, persistence });
  return StoreConfig(storeOptions);
}
